<script>
	import { onMount } from "svelte";
	import uuid4 from "uuid4";

	import FormRow from "./FormRow.svelte";
	import FormColumn from "./FormColumn.svelte";
	import Country from "./Country.svelte";
	import { validator } from "../validation";

	export let idPrefix = "a" + uuid4().substring(0, 3);
	export let googlePlaceJson = null;
	export let formattedAddress = null;
	export let subpremise = null;
	export let premise = null;
	export let street = null;
	export let suburb = null;
	export let state = null;
	export let postcode = null;
	export let country = null;
	export let lat = null;
	export let lng = null;
	export let placeId = null;
	export let url = null;
	export let disabled;
	export let required;

	let addressInput;
	let autocomplete;
	let uniqueId = "i" + uuid4().substring(0, 3);

	const australianStates = {
		"Australian Capital Territory": "ACT",
		"New South Wales": "NSW",
		"Northern Territory": "NT",
		Queensland: "QLD",
		"South Australia": "SA",
		Tasmania: "TAS",
		Victoria: "VIC",
		"Western Australia": "WA"
	};

	const updateAddressLine = (subpremise, premise, street) => {
		let addressLine;
		if (subpremise) {
			addressLine = subpremise;
		}
		if (premise) {
			addressLine = addressLine ? `${addressLine}, ${premise}` : premise;
		}
		if (street) {
			addressLine = addressLine ? `${addressLine}, ${street}` : street;
		}
		if (addressLine && addressInput) addressInput.value = addressLine;
	};

	$: {
		updateAddressLine(subpremise, premise, street);
	}

	$: {
		formattedAddress = "";
		if (subpremise) {
			formattedAddress = subpremise;
		}
		if (premise) {
			formattedAddress = formattedAddress ? `${formattedAddress}, ${premise}` : premise;
		}
		if (street) {
			formattedAddress = formattedAddress ? `${formattedAddress}, ${street}` : street;
		}
		if (suburb) {
			formattedAddress = formattedAddress ? `${formattedAddress}, ${suburb}` : suburb;
		}
		if (state) {
			formattedAddress = formattedAddress ? `${formattedAddress} ${state}` : state;
		}
		if (postcode) {
			formattedAddress = formattedAddress ? `${formattedAddress} ${postcode}` : postcode;
		}
		if (country) {
			formattedAddress = formattedAddress ? `${formattedAddress}, ${country}` : country;
		}
	}

	let googleError = false;

	function initiaise() {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(function (position) {
				var geolocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				};
				var circle = new google.maps.Circle({ center: geolocation, radius: position.coords.accuracy });
				autocomplete.setBounds(circle.getBounds());
			});
		}
	}

	function clearErrorDisplay() {
		let section = document.querySelector(`#${uniqueId}`);
		section && $validator.clearAllVisualError(section);
	}

	onMount(() => {
		updateAddressLine(subpremise, premise, street);

		if (!disabled) {
			try {
				const defaultBounds = new google.maps.LatLngBounds(
					new google.maps.LatLng(-37.812474, 144.956944),
					new google.maps.LatLng(-37.812474, 144.956944)
				);

				const options = {
					bounds: defaultBounds,
					types: ["geocode"]
				};

				//  In case there is no selection made
				addressInput.addEventListener("change", (e) => {
					street = e.target.value;
				});

				autocomplete = new google.maps.places.Autocomplete(addressInput, options);

				google.maps.event.addListener(autocomplete, "place_changed", function () {
					var result = autocomplete.getPlace();

					if (result) {
						var components = {};
						result.address_components.forEach((item, i) => {
							components[item.types[0]] = item.long_name;
						});
						googlePlaceJson = JSON.stringify(components);

						subpremise = components["subpremise"] || null;
						premise = components["premise"] || null;
						street =
							(components["street_number"] ? components["street_number"] + " " : "") +
							(components["route"] ? components["route"] : "");
						suburb = components["locality"] || "";
						state = components["administrative_area_level_1"] || "";
						postcode = components["postal_code"] || "";
						country = components["country"];

						if (australianStates[state]) {
							state = australianStates[state];
						}

						formattedAddress = result.formatted_address;

						if (result.geometry) {
							lat = result.geometry.location.lat();
							lng = result.geometry.location.lng();
						}
						placeId = result.place_id;
						url = result.url;

						$validator.validate(addressInput);
						clearErrorDisplay();

						result = {};
					}
				});
			} catch (error) {
				console.error("Mapping Error:", error);
				googleError = true;
			}
		}
	});
</script>

<section id={uniqueId}>
	<FormRow class="form-group form-row">
		<FormColumn cols="8">
			<label for="{idPrefix}_address" class:isrequired={required}>Address</label>
			{#if googleError}
				<input
					{disabled}
					type="text"
					bind:value={street}
					class="form-control"
					{required}
					id="{idPrefix}_street"
				/>
			{:else}
				<input
					{disabled}
					type="text"
					bind:this={addressInput}
					class="form-control"
					on:focus={initiaise}
					{required}
					id="{idPrefix}_address"
				/>
			{/if}
		</FormColumn>
		<FormColumn cols="4">
			<label for="{idPrefix}_suburb" class:isrequired={required}>Suburb</label>
			<input {disabled} type="text" bind:value={suburb} class="form-control" {required} id="{idPrefix}_suburb" />
		</FormColumn>
	</FormRow>

	<FormRow class="form-group form-row">
		<FormColumn cols="4">
			<label for="{idPrefix}_state" class:isrequired={required}>State</label>
			<input {disabled} type="text" bind:value={state} class="form-control" {required} id="{idPrefix}_state" />
		</FormColumn>
		<FormColumn cols="4">
			<label for="{idPrefix}_postcode" class:isrequired={required}>Postcode</label>
			<input
				{disabled}
				type="text"
				bind:value={postcode}
				class="form-control"
				{required}
				id="{idPrefix}_postcode"
			/>
		</FormColumn>
		<FormColumn cols="4">
			<label for="{idPrefix}_country" class:isrequired={required}>Country</label>
			<Country {disabled} bind:value={country} {required} id="{idPrefix}_country"></Country>
		</FormColumn>
	</FormRow>
</section>
